import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import Victory from '@/victory';
import { GraphItemData } from './types';
import { PanResponder, View } from 'react-native';
import { useTheme } from '@/contexts/theme-context';

interface PriceGraphProps {
  formatValue: (value: number) => string;
  graphData: GraphItemData[];
  lastValue: number | null;
  median: number;
}
const L_BAR_WIDTH = 16;
const M_BAR_WIDTH = 8;
const S_BAR_WIDTH = 4;

export const PriceGraph: React.FC<PriceGraphProps> = ({
  graphData,
  lastValue,
  median,
  formatValue,
}) => {
  const theme = useTheme();

  const [activatedData, setActivatedData] = useState(null);
  const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);

  const chartStyles = {
    axisStyle: {
      axis: { stroke: theme.colors.transparent },
      tickLabels: {
        fill: theme.colors.inactiveText,
        fontFamily: theme.fonts.primary,
        fontSize: 11,
        lineHeight: 12,
        padding: 0,
      },
    },
    barStyle: {
      fill: theme.colors.stroke,
      stroke: theme.colors.black,
    },
    chart: {
      height: 216,
      padding: {
        bottom: 32,
        left: 32,
        right: 24,
        top: 32,
      },
    },
    legendLabelStyle: {
      fill: theme.colors.inactiveText,
      fontFamily: theme.fonts.primary,
      fontSize: 12,
    },
    tooltipLableStyle: { fontFamily: theme.fonts.primarySemiBold },
    tooltipStyle: {
      fill: theme.colors.white,
      stroke: theme.colors.divider,
    },
  };

  const yAxisTickValues = useMemo(() => {
    // This function calculates the y-axis ticks based on the maximum y value in the graph data.
    // The max tick value is rounded to the nearest 100 if the maxY is greater than 500.
    // The max tick value is rounded to the nearest 20 if the maxY is greater than 100.
    // The max tick value is rounded to the nearest 10 if the maxY is greater than 20.
    // If the maximum y value is less than 20, the ticks are calculated in increments of 5.
    // If the maximum y value is less than 5, the ticks are calculated in increments of 1.
    const maxY = Math.max(...graphData.map((item) => item.y));
    let ticks = [];
    if (maxY >= 500) {
      const yRounded = Math.round(maxY / 100) * 100;
      ticks = [0, yRounded * 0.25, yRounded * 0.5, yRounded * 0.75, yRounded];
    } else if (maxY >= 100) {
      const yRounded = Math.round(maxY / 20) * 20;
      ticks = [0, yRounded * 0.25, yRounded * 0.5, yRounded * 0.75, yRounded];
    } else if (maxY >= 20) {
      const yRounded = Math.round(maxY / 10) * 10;
      if (yRounded % 4 === 0) {
        ticks = [0, yRounded * 0.25, yRounded * 0.5, yRounded * 0.75, yRounded];
      } else if (yRounded % 3 === 0) {
        ticks = [0, yRounded * (1 / 3), yRounded * (2 / 3), yRounded];
      } else {
        ticks = [0, yRounded * 0.5, yRounded];
      }
    } else if (maxY >= 5) {
      const yRounded = Math.round(maxY / 5) * 5;
      for (let tick = 0; tick <= yRounded; tick += 5) {
        ticks.push(tick);
      }
    } else {
      for (let i = 0; i <= maxY; i++) {
        ticks.push(i);
      }
    }
    return ticks;
  }, [graphData]);

  const legendData = useMemo(() => {
    // This function calculates the legend data based on the median and last purchase price.
    const data = [
      {
        name: i18n.t('median', {
          value: formatValue(median),
        }),
        symbol: { fill: 'white', stroke: 'black' },
      },
    ];
    if (lastValue && graphData.find((item) => item.x === String(lastValue))) {
      data.unshift({
        name: i18n.t('last', {
          value: formatValue(lastValue),
        }),
        symbol: { fill: 'black', stroke: 'black' },
      });
    }
    return data;
  }, [formatValue, graphData, lastValue, median]);

  const barWidth = useMemo(() => {
    if (!chartWidth) {
      return S_BAR_WIDTH;
    }
    const unpaddedChartWidth =
      chartWidth -
      chartStyles.chart.padding.left -
      chartStyles.chart.padding.right;
    if (graphData.length * L_BAR_WIDTH < unpaddedChartWidth) {
      return L_BAR_WIDTH;
    } else if (graphData.length * M_BAR_WIDTH < unpaddedChartWidth) {
      return M_BAR_WIDTH;
    } else {
      return S_BAR_WIDTH;
    }
  }, [
    chartStyles.chart.padding.left,
    chartStyles.chart.padding.right,
    chartWidth,
    graphData.length,
  ]);

  const minimumBarHeight = useMemo(() => {
    // This function calculates the minimum bar height based on the maximum y value in the graph data.
    // Without this calculation, short bars look awkwardly short.
    const maxY = Math.max(...graphData.map((item) => item.y));
    if (barWidth === L_BAR_WIDTH) {
      return maxY / 5;
    } else if (barWidth === M_BAR_WIDTH) {
      return maxY / 16;
    }
    return maxY / 34;
  }, [barWidth, graphData]);

  const cornerRadii = useMemo(() => {
    const radius = barWidth / 2;
    return {
      bottomLeft: radius,
      bottomRight: radius,
      topLeft: radius,
      topRight: radius,
    };
  }, [barWidth]);

  const calculateXYDomain = (): {
    x: [number, number];
    y?: [number, number];
  } => {
    const minDomainX = 0.9; // Default minimum x domain
    const maxDomainX = graphData.length + 0.1;

    const maxDomainY = Math.max(...graphData.map((item) => item.y));
    // Calculate the nearest rounded value of maxDomainY
    let domainYFactor = 0;
    const nearestRoundedValue = Math.pow(
      10,
      Math.floor(Math.log10(maxDomainY)),
    );
    const roundedMaxDomainY =
      Math.ceil(maxDomainY / nearestRoundedValue) * nearestRoundedValue;

    // Calculate the factor for the y domain
    domainYFactor = roundedMaxDomainY / 10;
    // The factor is multiplied by 2.1 to give some space for the legend
    const legendFactor = 2.1;
    domainYFactor = domainYFactor * legendFactor;

    return {
      x: [minDomainX, maxDomainX],
      y: [0, maxDomainY + domainYFactor],
    };
  };

  const getXAxisTickValues = (tick: string) => {
    // This function formats the x-axis tick values.
    // The first and last tick values are formatted as currency.
    // The rest of the tick values are empty strings.
    if (tick === graphData[0].x) {
      return formatValue(Number(tick));
    }
    if (tick === graphData[graphData.length - 1].x) {
      return formatValue(Number(tick));
    }
    return '';
  };

  const getBarColor = (datum: GraphItemData) => {
    if (datum.x === activatedData) {
      return theme.colors.accent;
    }
    if (median && datum.x === String(median)) {
      return theme.colors.white;
    }
    if (datum.x === String(lastValue)) {
      return theme.colors.black;
    }
    return chartStyles.barStyle.fill;
  };

  const getBarStrokeWidth = (datum: GraphItemData) => {
    if (median && datum.x === String(median)) {
      return 1;
    }
    return 0;
  };

  // PanResponder is used to stop propagation of touch events
  // when the user interacts with the graph.
  // For an in-depth explanation, refer to the following PR:
  // https://github.com/usepepper/restaurant-app/pull/1681
  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e) => {
        e.stopPropagation();
      },
      onPanResponderMove: (e) => {
        e.stopPropagation();
      },
      onPanResponderRelease: (e) => {
        setActivatedData(null);
        e.stopPropagation();
      },
      onPanResponderTerminate: (e) => {
        setActivatedData(null);
        e.stopPropagation();
      },
      onStartShouldSetPanResponder: () => true,
    }),
  ).current;

  return (
    <View
      accessibilityLabel={i18n.t('price_trends_graph')}
      onLayout={(event) => {
        setChartWidth(event.nativeEvent.layout.width);
      }}
      onPointerLeave={() => setActivatedData(null)}
      onTouchCancel={() => setActivatedData(null)}
      onTouchEnd={() => setActivatedData(null)}
      style={{ height: chartStyles.chart.height }}
      {...panResponder.panHandlers}
    >
      <Victory.VictoryChart
        containerComponent={
          <Victory.VictoryVoronoiContainer
            onActivated={(points) => {
              setActivatedData(points[0]?.x);
            }}
            voronoiDimension="x"
          />
        }
        domain={calculateXYDomain()}
        height={chartStyles.chart.height}
        padding={chartStyles.chart.padding}
        width={chartWidth}
      >
        <Victory.VictoryAxis
          crossAxis={false}
          dependentAxis
          fixLabelOverlap
          style={chartStyles.axisStyle}
          tickLabelComponent={<Victory.VictoryLabel textAnchor="start" x={0} />}
          tickValues={yAxisTickValues}
        />
        <Victory.VictoryLegend
          data={legendData}
          gutter={20}
          labelComponent={
            <Victory.VictoryLabel style={chartStyles.legendLabelStyle} />
          }
          orientation="horizontal"
          titleComponent={<Victory.VictoryLabel />}
          x={-8}
        />
        <Victory.VictoryBar
          barWidth={barWidth}
          cornerRadius={cornerRadii}
          data={graphData}
          labelComponent={
            <Victory.VictoryTooltip
              constrainToVisibleArea
              dy={-8}
              flyoutStyle={chartStyles.tooltipStyle}
              labelComponent={
                <Victory.VictoryLabel style={chartStyles.tooltipLableStyle} />
              }
              pointerLength={0}
              renderInPortal={false}
            />
          }
          padding={{ top: 0 }}
          style={{
            data: {
              ...chartStyles.barStyle,
              fill: ({ datum }) => getBarColor(datum),
              strokeWidth: ({ datum }) => getBarStrokeWidth(datum),
            },
          }}
          y={(datum) => Math.max(datum.y, minimumBarHeight)}
        />
        <Victory.VictoryAxis
          crossAxis
          fixLabelOverlap
          style={chartStyles.axisStyle}
          tickFormat={getXAxisTickValues}
          tickLabelComponent={<Victory.VictoryLabel dy={12} />}
        />
      </Victory.VictoryChart>
    </View>
  );
};
